import BasicUtil from './basicUtil'

/**
 * 外部サイト（FP3級／FP2級サービス）からの遷移で「試験1」を解放するための
 * 署名トークン検証ユーティリティ。
 *
 * 外部サイトのサーバーが ES256(ECDSA P-256 / SHA-256) の秘密鍵で署名した JWT を
 * URL フラグメント（#t=...）に載せて遷移してくる。ここでは対になる「公開鍵」だけを
 * 持ち、署名と有効期限を検証する。公開鍵は誰に見られても署名を偽造できないため、
 * フロントへ埋め込んでよい。
 *
 * 検証成功時は対象級の試験1だけを静かに解放する（画面には何も表示しない）。
 */

// 外部サイトの秘密鍵に対応する公開鍵（P-256 / JWK 形式）。公開して問題ない。
// ※ 鍵ペア生成後、x / y を実際の値へ差し替えること（README/指示参照）。
const UNLOCK_PUBLIC_JWK: JsonWebKey = {
  kty: 'EC',
  crv: 'P-256',
  x: 'TN1e4XBp-kb1E8WhgooTkjXsmGHc2pYTh6IZtKjHsGc',
  y: '-AcrEVn_4JfFrM7gm3yYcheRSPNacPoRA2PPHIij61I',
}

// 同一ブラウザでのトークン再利用を拒否するための使用済み jti 記録キー
const USED_JTI_KEY = 'tac-cbt-ut'

function b64urlToBytes(s: string): Uint8Array {
  s = s.replace(/-/g, '+').replace(/_/g, '/')
  const pad = s.length % 4 === 0 ? '' : '='.repeat(4 - (s.length % 4))
  const bin = atob(s + pad)
  const out = new Uint8Array(bin.length)
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i)
  return out
}

function decodeJson(seg: string): any {
  return JSON.parse(new TextDecoder().decode(b64urlToBytes(seg)))
}

/**
 * ES256 JWT の署名と exp を検証する。問題があれば null を返す。
 */
async function verifyEs256(
  token: string
): Promise<{ g?: string; jti?: string; exp?: number; aud?: string } | null> {
  const parts = token.split('.')
  if (parts.length !== 3) return null

  let header: any
  try {
    header = decodeJson(parts[0])
  } catch {
    return null
  }
  if (header.alg !== 'ES256') return null

  const key = await crypto.subtle.importKey(
    'jwk',
    UNLOCK_PUBLIC_JWK,
    { name: 'ECDSA', namedCurve: 'P-256' },
    false,
    ['verify']
  )
  const data = new TextEncoder().encode(parts[0] + '.' + parts[1])
  const ok = await crypto.subtle.verify(
    { name: 'ECDSA', hash: 'SHA-256' },
    key,
    b64urlToBytes(parts[2]),
    data
  )
  if (!ok) return null

  let payload: any
  try {
    payload = decodeJson(parts[1])
  } catch {
    return null
  }
  const now = Math.floor(Date.now() / 1000)
  if (typeof payload.exp !== 'number' || now >= payload.exp) return null
  return payload
}

/**
 * URL フラグメント文字列（例: "#t=xxx" や "#foo&t=xxx"）からトークンを取り出す。
 */
function extractToken(hash: string): string | null {
  const h = hash.replace(/^#/, '')
  for (const pair of h.split('&')) {
    const idx = pair.indexOf('=')
    const k = idx === -1 ? pair : pair.slice(0, idx)
    const v = idx === -1 ? '' : pair.slice(idx + 1)
    if (k === 't' && v) {
      try {
        return decodeURIComponent(v)
      } catch {
        return v
      }
    }
  }
  return null
}

/**
 * フラグメントに署名トークンがあれば検証し、対象級の試験1だけを解放する。
 * 画面には何も表示せず、成否に関わらず静かに処理する（サイレント）。
 *
 * @returns 解放（または既に解放済み）の対象級 "3" / "2"。無効・対象外なら null。
 *          呼び出し側はこの級の試験TOP（/3・/2）へ着地させる。
 */
export async function tryUnlockFromToken(hash: string): Promise<string | null> {
  const token = extractToken(hash)
  if (!token) return null
  try {
    const payload = await verifyEs256(token)
    if (!payload) return null

    const g = String(payload.g ?? '')
    const jti = String(payload.jti ?? '')
    // 試験1の対象級のみ（FP3級="3" / FP2級="2"）。試験2(33/22)は解放しない。
    if (g !== '3' && g !== '2') return null

    // 環境（ホスト）バインド: 発行元が想定した CBT ホスト（aud）と現在のホストが
    // 一致しないトークンは拒否する。署名鍵が stg/prod で共通でも、stg 発行トークンを
    // prod（別ホスト）へリプレイすることを防ぐ。
    if (String(payload.aud ?? '') !== window.location.hostname) return null

    // 同一ブラウザでのトークン再利用を拒否（再利用でも既に解放済みなので級は返す）
    let used: string[] = []
    try {
      used = JSON.parse(localStorage.getItem(USED_JTI_KEY) || '[]')
    } catch {
      used = []
    }
    if (jti && used.includes(jti)) return g

    BasicUtil.setGradePass(g)

    if (jti) {
      used.push(jti)
      try {
        localStorage.setItem(USED_JTI_KEY, JSON.stringify(used.slice(-200)))
      } catch {
        /* localStorage 不可でも解放自体は成立しているので無視 */
      }
    }
    return g
  } catch {
    // 署名検証や JSON 解析の失敗はすべて無視（サイレント）
    return null
  }
}

/**
 * フラグメントに解放トークン（t=...）が含まれているか。
 */
export function hasUnlockToken(hash: string): boolean {
  return /(^#|&)t=/.test(hash)
}
